// packages/web/src/components/account/auth-magic-verify.tsx
/**
 * MagicVerify — consume a login magic-link token from the email link.
 *
 * 19BO+ CROSS-ORIGIN-AUTH: the magic-link email now points at the SPA
 * (`?magic-token=…`) instead of the worker. App root reads the token from
 * the URL and opens the modal in 'magicVerify' mode; this component calls
 * /auth/magic/verify via fetch, then reads the Set-Cookie from the credentialed
 * cross-origin response. On success we refresh currentUser from /auth/me +
 * close the modal. On failure (invalid/expired/used token) we show a retry
 * prompt that routes back to the magic-link input.
 *
 * Structurally mirrors AuthSignupVerify — same verifying/success/failed state
 * machine, different API call + copy. Kept as a separate component (not
 * abstracted) because the two flows have different success/failure semantics
 * (signup-verify can 409 on a registration race; magic-verify can 429 on
 * account lockout) and the copy differs.
 */

import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import { useStore } from '../../lib/store/index.js';
import type { AuthMode } from './auth-modal.js';

interface Props {
  token: string;
  onSwitchMode: (m: AuthMode) => void;
  onClose: () => void;
}

type State = 'verifying' | 'success' | 'failed';

export function AuthMagicVerify({ token, onSwitchMode, onClose }: Props) {
  const t = useT();
  const [state, setState] = useState<State>('verifying');
  const refreshFromServer = useStore((s) => s.refreshFromServer);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await authApi.magicVerify(token);
        if (cancelled) return;
        // Cookie is now set; refresh currentUser from /auth/me (triggers the
        // login side effects — anon→email migration + notification).
        await refreshFromServer();
        if (cancelled) return;
        setState('success');
        clearMagicTokenParam();
        setTimeout(() => onClose(), 1200);
      } catch {
        if (cancelled) return;
        setState('failed');
        clearMagicTokenParam();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token is stable per mount
  }, [token]);

  if (state === 'verifying') {
    return (
      <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
        <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-accent)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
          {t('auth.magicVerifyPending')}
        </h3>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
        <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-success-deep)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
          {t('auth.magicVerifySuccess')}
        </h3>
      </div>
    );
  }

  // state === 'failed'
  return (
    <div className="auth-body" style={{ textAlign: 'center', padding: 'var(--space-4xl) var(--space-3xl)' }}>
      <div className="auth-magic-sent-icon" style={{ background: 'var(--color-success-soft)', color: 'var(--color-accent)' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-ink)', margin: 'var(--space-xl) 0 0' }}>
        {t('auth.magicVerifyFailed')}
      </h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: 'var(--space-md) 0 0' }}>
        {t('auth.magicVerifyFailedHint')}
      </p>
      <div className="auth-switch" style={{ marginTop: 'var(--space-2xl)' }}>
        <button type="button" onClick={() => onSwitchMode('magic')}>← {t('auth.magicLinkLogin')}</button>
      </div>
    </div>
  );
}

/** Remove ?magic-token= from the URL without triggering a navigation. */
function clearMagicTokenParam(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('magic-token');
  window.history.replaceState({}, '', url);
}
